import React, { useState, useEffect } from 'react'
import { Container, Image } from 'react-bootstrap'
import moment from 'moment'
import 'flatpickr/dist/flatpickr.css'
import { CLASSES } from '../../../assets/styles/classes'
import PlusIcon from '../../../assets/icons/common/plus-white.svg'
import CalendarWhiteIcon from '../../../assets/icons/sidebar/calendar-minus.svg'
import Table from '../../../components/table/Table'
import { useDispatch, useSelector } from 'react-redux'
import getPaginationData, {
  deletePaginationData,
} from '../../../store/actions/pagination'
import SearchFilter from '../../../components/extras/SearchFilter'
import { useNavigate } from 'react-router-dom'
import ActionElements from '../../../components/extras/ActionElements'
import { PAGINATION_TYPES } from '../../../utils/constants'
import { AppointmentService } from '../../../services'
import { setCurrentPage } from '../../../store/reducers'
import REST_API from '../../../services/API_Routes'
import { APPOINTMENT_STATUS_OPTIONS } from '../../../utils/static_data'
import ButtonWithIcon from '../../../components/common/ButtonWithIcon'
import { USER_ROLES } from '../../../utils/userRole'
import { preConfirmAlert } from '../../../utils/helperFunctions'

const columns = (navigate, deleteClick, canAccess) => [
  {
    name: 'Time',
    width: '130px',
    center: true,
    style: {
      fontWeight: 'bold',
    },
    style: { borderRight: '2px solid #C8C8C8' },
    cell: ({ appointment_time }) => (
      <div className='bg-blue text-white p-2'>
        {moment(appointment_time, 'HH:mm:ss').format('hh:mm A')}
      </div>
    ),
  },
  {
    name: 'Appointment Id',
    width: '180px',
    style: {
      fontWeight: 'bold',
    },
    cell: ({ appointment_unique_id }) => appointment_unique_id,
  },
  {
    name: 'Customer Name',
    minWidth: '180px',
    cell: ({ customer }) => {
      if (customer) {
        return customer.customer_last_name
          ? `${customer.customer_first_name} ${customer.customer_last_name}`
          : customer.customer_first_name
      }
      return ''
    },
  },
  {
    name: 'Email',
    minWidth: '250px',
    cell: ({ customer }) =>
      customer.customer_email_1
        ? customer.customer_email_1
        : customer.customer_email_2,
  },
  {
    name: 'Phone Number',
    // minWidth: '180px',
    cell: ({ customer }) =>
      customer.customer_phone1
        ? customer.customer_phone1
        : customer.customer_phone2,
  },
  {
    name: 'Date',
    minWidth: '140px',
    center: true,
    cell: ({ appointment_date }) =>
      moment(appointment_date).format('DD MMM, YYYY'),
  },
  {
    name: 'Type',
    width: '180px',
    center: true,
    cell: ({ appointment_type }) => (
      <div
        style={{ borderRadius: 20, width: 120, backgroundColor: '#FEF4EA' }}
        className={'p-1 text-center text-black flex-shrink-0'}
      >
        {appointment_type}
      </div>
    ),
  },
  {
    name: 'Status',
    // minWidth: '150px',
    center: true,
    cell: ({ status }) => (
      <div
        style={{ borderRadius: 20, width: 120, border: '1px solid #F3922F' }}
        className={
          ' p-1 text-center flex-shrink-0' +
          ' border-' +
          APPOINTMENT_STATUS_OPTIONS.find(
            (item) => item.value == status.appointment_status_id,
          ).color +
          ' text-' +
          APPOINTMENT_STATUS_OPTIONS.find(
            (item) => item.value == status.appointment_status_id,
          ).color
        }
      >
        {status ? status.appointment_status_name : 'Unknown'}{' '}
        {/* Defaulting to 'Unknown' if status is not present */}
      </div>
    ),
  },
  {
    name: 'Actions',
    width: '120px',
    center: true,
    omit: !canAccess,
    cell: (row) => (
      <ActionElements
        id={row.appointment_id || 1}
        editClick={() => navigate(`/appointment/edit/${row.appointment_id}`)}
        deleteClick={() => deleteClick(row.appointment_id)}
      />
    ),
  },
]

export default function Appointments() {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { user_role } = useSelector(({ auth }) => auth)

  const { items, currentPage, rowsPerPage } = useSelector(
    ({ pagination }) => pagination[PAGINATION_TYPES.APPOINTMENTS],
  )
  const isLoading = useSelector(
    ({ loader }) => loader[PAGINATION_TYPES.APPOINTMENTS],
  )
  const [searchQuery, setSearchQuery] = useState({})

  useEffect(() => {
    dispatch(
      getPaginationData({
        key: PAGINATION_TYPES.APPOINTMENTS,
        ServiceName: AppointmentService,
        page: currentPage,
        per_page: rowsPerPage,
        ...searchQuery,
      }),
    )
  }, [currentPage, rowsPerPage, searchQuery])

  const handleSearch = (query) => {
    setSearchQuery(query)
    dispatch(
      setCurrentPage({
        key: PAGINATION_TYPES.APPOINTMENTS,
        currentPage: 1,
      }),
    )
  }

  const deleteClick = async (id) => {
    const { isConfirmed } = await preConfirmAlert({
      title: 'Are you sure to delete?',
      text: "You won't be able to revert this!",
      confirmButtonText: 'Yes, Delete it',
      denyButtonText: 'No',
      confirmButton: 'btn btn-danger px-5',
      denyButton: 'btn btn-primary ms-3 px-5',
    })
    if (isConfirmed) {
      dispatch(
        deletePaginationData({
          id,
          key: PAGINATION_TYPES.APPOINTMENTS,
          ServiceName: AppointmentService,
          primaryKey: 'appointment_id',
          route: REST_API.APPOINTMENT_DELETE + id,
          value: { appointment_status_id: 0 }, // Assuming a value to mark as deleted
        }),
      )
    }
  }

  const canAccess = user_role === USER_ROLES.ADMIN

  return (
    <Container fluid className='p-4 py-2 mt-2'>
      <div>
        <div className={CLASSES.content_between + 'flex-wrap gap-3'}>
          <h4 className={CLASSES.items_center + 'mb-0'}>
            <Image src={CalendarWhiteIcon} className='me-3' /> Appointments
          </h4>

          {canAccess && (
            <ButtonWithIcon
              onClick={() => navigate('/appointment/create')}
              className={'border-0'}
              label='Add New Appointment'
              icon={PlusIcon}
            />
          )}
        </div>

        <div className='mb-5 mt-3 pt-4 border-radius-10 black-shadow-15'>
          <div className={CLASSES.content_between + ' px-4 mb-4'}>
            <SearchFilter
              handleSearch={handleSearch}
              placeholder={'Appointment ID'}
            />
          </div>

          <Table
            columns={columns(navigate, deleteClick, canAccess)}
            data={items}
            progressPending={isLoading}
            paginationType={PAGINATION_TYPES.APPOINTMENTS}
            customStyle={{
              rows: {
                style: {
                  borderTop: '0px solid #E0E0E0',
                  borderBottom: '1px solid #E0E0E0 !important',
                  display: 'flex !important',
                  alignItems: 'center !important',
                  padding: 5,
                },
              },
            }}
          />
        </div>
      </div>
    </Container>
  )
}
